import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { X, Download, Eye, Loader2 } from 'lucide-react';
import { deleteImage } from '@/services/productManagerImageService';

interface ImageGalleryProps {
  images: string[];
  onImageDeleted?: (imageUrl: string) => void;
  canDelete?: boolean;
  className?: string;
  showDownload?: boolean;
  deleteFromStorage?: boolean; // New prop to control whether to delete from Firebase Storage
}

interface ImageItemProps {
  src: string;
  onDelete?: () => void;
  canDelete?: boolean;
  showDownload?: boolean;
  deleteFromStorage?: boolean; // New prop to control whether to delete from Firebase Storage
}

const ImageItem: React.FC<ImageItemProps> = ({
  src,
  onDelete,
  canDelete = false,
  showDownload = true,
  deleteFromStorage = true
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showFullSize, setShowFullSize] = useState(false);

  const handleImageLoad = () => {
    setLoading(false);
  };

  const handleImageError = () => {
    setLoading(false);
    setError(true);
  };

  const handleDelete = async () => {
    if (!onDelete || deleting) return;
    
    try {
      setDeleting(true);
      
      // Only delete from Firebase Storage if deleteFromStorage is true
      if (deleteFromStorage) {
        await deleteImage(src);
      }
      
      onDelete();
    } catch (error) {
      console.error('Error deleting image:', error);
      setDeleting(false);
    }
  };

  const handleDownload = () => {
    // Simply open the image in a new window/tab
    window.open(src, '_blank', 'noopener,noreferrer');
  };

  if (error) {
    return (
      <Card className="w-32 h-32 flex items-center justify-center bg-gray-100 dark:bg-gray-800">
        <CardContent className="p-2 text-center">
          <p className="text-xs text-gray-500">Failed to load image</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="relative group overflow-hidden">
        <CardContent className="p-0">
          <div className="relative w-32 h-32">
            {loading && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-800">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            )}
            
            <img
              src={src}
              alt="Uploaded image"
              className={`w-full h-full object-cover cursor-pointer transition-opacity ${
                loading ? 'opacity-0' : 'opacity-100'
              }`}
              onLoad={handleImageLoad}
              onError={handleImageError}
              onClick={() => setShowFullSize(true)}
            />
            
            {/* Overlay with actions */}
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
              <div className="flex space-x-2">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setShowFullSize(true)}
                  className="h-8 w-8 p-0"
                >
                  <Eye className="h-4 w-4" />
                </Button>
                
                {showDownload && (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={handleDownload}
                    className="h-8 w-8 p-0"
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                )}
                
                {canDelete && (
                  <Button
                    type="button"
                    size="sm"
                    variant="destructive"
                    onClick={handleDelete}
                    disabled={deleting}
                    className="h-8 w-8 p-0"
                  >
                    {deleting ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <X className="h-4 w-4" />
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Full Size Modal */}
      {showFullSize && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
          onClick={() => setShowFullSize(false)}
        >
          <div className="relative max-w-4xl max-h-full">
            <Button
              variant="secondary"
              size="sm"
              className="absolute top-4 right-4 z-10"
              onClick={() => setShowFullSize(false)}
            >
              <X className="h-4 w-4" />
            </Button>
            
            <img
              src={src}
              alt="Full size image"
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            
            <div className="absolute bottom-4 left-4 right-4 flex justify-center space-x-2">
              {showDownload && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleDownload}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </Button>
              )}
              
              {canDelete && (
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={handleDelete}
                  disabled={deleting}
                >
                  {deleting ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <X className="h-4 w-4 mr-2" />
                  )}
                  Delete
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const ImageGallery: React.FC<ImageGalleryProps> = ({
  images,
  onImageDeleted,
  canDelete = false,
  className = '',
  showDownload = true,
  deleteFromStorage = true
}) => {
  if (!images || images.length === 0) {
    return null;
  }

  return (
    <div className={`space-y-3 ${className}`}>
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-900 dark:text-white">
          Images ({images.length})
        </h4>
      </div>
      
      <div className="flex flex-wrap gap-3">
        {images.map((imageUrl, index) => (
          <ImageItem
            key={`${imageUrl}-${index}`}
            src={imageUrl}
            onDelete={onImageDeleted ? () => onImageDeleted(imageUrl) : undefined}
            canDelete={canDelete}
            showDownload={showDownload}
            deleteFromStorage={deleteFromStorage}
          />
        ))}
      </div>
    </div>
  );
};

export default ImageGallery;